// Geodesic-distance skin-weight computation for auto-rigging (docs/3d-
// content-pipeline/roadmap.md 3.3 - "heat diffusion (bone heat)" auto-
// skinning, Baran & Popović SIGGRAPH 2007, the technique Blender's own
// "Armature Deform, With Automatic Weights" has shipped since 2.4x).
//
// This is a geodesic-distance APPROXIMATION of bone heat, not a literal
// reproduction of the paper's own linear system: the real method solves a
// discrete heat-equilibrium equation (a sparse Laplacian solve per bone) -
// correct, but a sparse linear solver is a large, easy-to-get-subtly-wrong
// piece of numerical code to write and verify from scratch in a browser-side
// v1. Dijkstra's algorithm over the mesh's own edge graph keeps the one
// property that actually matters for correctness: influence only travels
// ALONG the surface, never in a straight line through empty space. That is
// exactly what makes bone heat (and this) correct where naive
// nearest-bone-by-Euclidean-distance fails - two fingers close together in
// 3D space but far apart along the surface - and it produces the same
// "zero weight where no surface path exists" behaviour on a disconnected
// mesh island that this feature's own known v1 upload constraint (section 4)
// already expects.
//
// `dijkstraFromSources` itself uses a real binary-heap priority queue (see
// `MinHeap` below), so it is the standard O((V+E) log V) Dijkstra, not the
// naive O(V^2) linear-scan extract-min - at the character-body vertex counts
// this feature targets (the bundled `animated-base-character.glb`'s own
// primitives run to several thousand vertices), an O(V^2) scan per bone is a
// genuine multi-hundred-ms-to-multi-second main-thread freeze, not a
// theoretical concern only reached at extreme scale. `computeGeodesicSkinWeights`
// (the actual entry point every caller uses) additionally runs as an async
// generator-driven loop that yields back to the browser between bones and in
// chunks across the final per-vertex weight pass, so a "Build rig" click
// (AutoRigPanel.tsx) can actually paint its "Building…" state and the tab
// stays responsive while it works, rather than blocking the main thread for
// the whole computation in one synchronous call.
import { Vector3 } from 'three';

/**
 * A minimal binary min-heap keyed by a caller-supplied priority, used as
 * Dijkstra's own priority queue below. Uses lazy deletion (an entry can be
 * pushed more than once for the same vertex as its distance improves; a
 * pop that turns out to be stale is simply skipped by the caller via the
 * `visited` check) rather than a decrease-key operation - simpler to get
 * right, and the extra stale entries never exceed the number of edge
 * relaxations, so this stays O(E log E) = O(E log V) overall.
 */
class MinHeap<T> {
  private readonly items: { priority: number; value: T }[] = [];

  get size(): number {
    return this.items.length;
  }

  push(priority: number, value: T): void {
    const items = this.items;
    let i = items.length;
    items.push({ priority, value });
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (items[parent]!.priority <= items[i]!.priority) break;
      const tmp = items[parent]!;
      items[parent] = items[i]!;
      items[i] = tmp;
      i = parent;
    }
  }

  pop(): { priority: number; value: T } | undefined {
    const items = this.items;
    if (items.length === 0) return undefined;
    const top = items[0]!;
    const last = items.pop()!;
    if (items.length > 0) {
      items[0] = last;
      let i = 0;
      for (;;) {
        const left = i * 2 + 1;
        const right = i * 2 + 2;
        let smallest = i;
        if (left < items.length && items[left]!.priority < items[smallest]!.priority) smallest = left;
        if (right < items.length && items[right]!.priority < items[smallest]!.priority) smallest = right;
        if (smallest === i) break;
        const tmp = items[smallest]!;
        items[smallest] = items[i]!;
        items[i] = tmp;
        i = smallest;
      }
    }
    return top;
  }
}

/** Yields control back to the browser's event loop (a macrotask boundary,
 * via `setTimeout`) so a pending paint/input event gets a chance to run -
 * `requestIdleCallback` would wait for genuine idle time, which under
 * sustained CPU load could delay the very "Building…" paint this exists to
 * unblock, so a plain macrotask yield is the more reliable choice here. */
function yieldToBrowser(): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, 0));
}

export interface MeshGraph {
  /** Flat vertex positions, 3 floats per vertex. */
  positions: Float32Array;
  vertexCount: number;
  /** Adjacency list: `neighbours[v]` is every vertex sharing a mesh edge
   * with `v`, paired with the Euclidean edge length. */
  neighbours: { vertex: number; distance: number }[][];
}

/** Builds an undirected adjacency graph from a triangle index list, one
 * entry per mesh edge (deduplicated - each triangle contributes 3 edges,
 * shared edges between adjacent triangles collapse to one). */
export function buildMeshGraph(positions: Float32Array, indices: ArrayLike<number>): MeshGraph {
  const vertexCount = positions.length / 3;
  const neighbours: { vertex: number; distance: number }[][] = Array.from({ length: vertexCount }, () => []);
  const seen = new Set<number>();

  const vertexAt = (i: number): Vector3 => new Vector3(positions[i * 3]!, positions[i * 3 + 1]!, positions[i * 3 + 2]!);

  const addEdge = (a: number, b: number) => {
    if (a === b) return;
    const key = a < b ? a * vertexCount + b : b * vertexCount + a;
    if (seen.has(key)) return;
    seen.add(key);
    const distance = vertexAt(a).distanceTo(vertexAt(b));
    neighbours[a]!.push({ vertex: b, distance });
    neighbours[b]!.push({ vertex: a, distance });
  };

  for (let i = 0; i + 2 < indices.length; i += 3) {
    const a = indices[i]!;
    const b = indices[i + 1]!;
    const c = indices[i + 2]!;
    addEdge(a, b);
    addEdge(b, c);
    addEdge(c, a);
  }

  return { positions, vertexCount, neighbours };
}

/**
 * Multi-source shortest-path distance to every vertex, via a real
 * binary-heap Dijkstra - O((V+E) log V), not the O(V^2) an unheaped
 * linear-scan extract-min would cost. Unreachable vertices (a disconnected
 * mesh island) keep `Infinity`, which callers treat as zero influence - the
 * documented v1 upload constraint.
 */
export function dijkstraFromSources(graph: MeshGraph, sources: { vertex: number; startDistance: number }[]): Float32Array {
  const dist = new Float32Array(graph.vertexCount).fill(Infinity);
  const visited = new Uint8Array(graph.vertexCount);
  const heap = new MinHeap<number>();

  for (const { vertex, startDistance } of sources) {
    if (startDistance < dist[vertex]!) dist[vertex] = startDistance;
  }
  for (const { vertex, startDistance } of sources) {
    heap.push(startDistance, vertex);
  }

  for (;;) {
    const entry = heap.pop();
    if (!entry) break;
    const u = entry.value;
    if (visited[u]) continue; // a stale, already-superseded queue entry
    visited[u] = 1;
    const du = dist[u]!;
    for (const { vertex: v, distance } of graph.neighbours[u]!) {
      if (visited[v]) continue;
      const candidate = du + distance;
      if (candidate < dist[v]!) {
        dist[v] = candidate;
        heap.push(candidate, v);
      }
    }
  }

  return dist;
}

/** The nearest mesh vertex to an arbitrary 3D point - used to seed Dijkstra
 * from a bone segment sample that doesn't necessarily land exactly on a
 * vertex. */
function nearestVertex(graph: MeshGraph, point: Vector3): number {
  let best = 0;
  let bestDist = Infinity;
  for (let v = 0; v < graph.vertexCount; v++) {
    const dx = graph.positions[v * 3]! - point.x;
    const dy = graph.positions[v * 3 + 1]! - point.y;
    const dz = graph.positions[v * 3 + 2]! - point.z;
    const d = dx * dx + dy * dy + dz * dz;
    if (d < bestDist) {
      bestDist = d;
      best = v;
    }
  }
  return best;
}

/** One canonical bone's influence region: a polyline (2 points = a straight
 * bone, 3+ = a bent limb like shoulder-elbow-wrist) sampled at intervals to
 * seed the geodesic search - the whole polyline is "distance zero" for this
 * bone, not just its two ends. */
export interface BoneInfluenceSegment {
  name: string;
  polyline: Vector3[];
}

const MAX_INFLUENCES_PER_VERTEX = 4;
const SAMPLES_PER_SEGMENT = 6;

/** How many vertices the final weight-assembly pass processes between
 * yields - small enough that even a slow device still paints a frame every
 * chunk, large enough that yielding itself stays a tiny fraction of the
 * total work. */
const VERTEX_CHUNK_SIZE = 1500;

/**
 * Per-vertex skin weights for `bones`, in the same 4-influence-per-vertex
 * shape three.js/glTF skinning expects (`skinIndex`/`skinWeight`
 * attributes): for every vertex, the geodesic distance to each bone's
 * polyline is turned into a weight via inverse-square falloff
 * (`1 / (distance + epsilon)^2`, the same falloff family bone heat's own
 * source term uses), the strongest `MAX_INFLUENCES_PER_VERTEX` bones are
 * kept, and those are renormalised to sum to 1. A vertex with no reachable
 * bone at all (fully disconnected from every marker) falls back to 100%
 * weight on the nearest bone by straight-line distance, rather than
 * producing a degenerate all-zero weight row that would make three.js
 * render that vertex collapsed to the skeleton's origin.
 *
 * Async, and yields back to the browser (via `yieldToBrowser`) between each
 * bone's own Dijkstra run and periodically across the final per-vertex
 * assembly pass below - `buildAutoRig`/`AutoRigPanel.tsx`'s "Build rig"
 * button awaits this, so the UI can paint its "Building…" state and stay
 * responsive instead of freezing the tab for the computation's full
 * duration in one synchronous call.
 */
export async function computeGeodesicSkinWeights(
  graph: MeshGraph,
  bones: BoneInfluenceSegment[],
): Promise<{ skinIndices: Uint16Array; skinWeights: Float32Array }> {
  if (bones.length === 0) throw new Error('computeGeodesicSkinWeights needs at least one bone.');

  const distancesByBone: Float32Array[] = [];
  for (const bone of bones) {
    const sources: { vertex: number; startDistance: number }[] = [];
    for (let i = 0; i < bone.polyline.length - 1; i++) {
      const a = bone.polyline[i]!;
      const b = bone.polyline[i + 1]!;
      for (let s = 0; s <= SAMPLES_PER_SEGMENT; s++) {
        const t = s / SAMPLES_PER_SEGMENT;
        const point = a.clone().lerp(b, t);
        sources.push({ vertex: nearestVertex(graph, point), startDistance: 0 });
      }
    }
    if (bone.polyline.length === 1) sources.push({ vertex: nearestVertex(graph, bone.polyline[0]!), startDistance: 0 });
    distancesByBone.push(dijkstraFromSources(graph, sources));
    // One bone's Dijkstra run at a time, then back to the browser - the
    // canonical rig has only seven bones, but each is now a real
    // O((V+E) log V) solve rather than free, so this still matters on a
    // large upload.
    await yieldToBrowser();
  }

  const skinIndices = new Uint16Array(graph.vertexCount * MAX_INFLUENCES_PER_VERTEX);
  const skinWeights = new Float32Array(graph.vertexCount * MAX_INFLUENCES_PER_VERTEX);
  const epsilon = 1e-4;

  for (let v = 0; v < graph.vertexCount; v++) {
    const candidates = bones.map((_, boneIndex) => ({ boneIndex, distance: distancesByBone[boneIndex]![v]! }));
    const reachable = candidates.filter((c) => Number.isFinite(c.distance));
    const pool = reachable.length > 0 ? reachable : candidates; // fallback: straight-line-nearest below

    let ranked = pool;
    if (reachable.length === 0) {
      // Nothing reachable by any surface path (a fully isolated vertex) -
      // fall back to whichever bone's nearest polyline SAMPLE is closest in
      // straight-line space, so the vertex still gets a sane single influence
      // instead of collapsing to the origin.
      const vx = graph.positions[v * 3]!;
      const vy = graph.positions[v * 3 + 1]!;
      const vz = graph.positions[v * 3 + 2]!;
      const point = new Vector3(vx, vy, vz);
      let bestBone = 0;
      let bestStraight = Infinity;
      bones.forEach((bone, boneIndex) => {
        for (const p of bone.polyline) {
          const d = p.distanceTo(point);
          if (d < bestStraight) {
            bestStraight = d;
            bestBone = boneIndex;
          }
        }
      });
      ranked = [{ boneIndex: bestBone, distance: 0 }];
    }

    ranked = [...ranked].sort((a, b) => a.distance - b.distance).slice(0, MAX_INFLUENCES_PER_VERTEX);
    const rawWeights = ranked.map((c) => 1 / (c.distance + epsilon) ** 2);
    const total = rawWeights.reduce((sum, w) => sum + w, 0) || 1;

    for (let slot = 0; slot < MAX_INFLUENCES_PER_VERTEX; slot++) {
      const entry = ranked[slot];
      const outIndex = v * MAX_INFLUENCES_PER_VERTEX + slot;
      skinIndices[outIndex] = entry ? entry.boneIndex : 0;
      skinWeights[outIndex] = entry ? rawWeights[slot]! / total : 0;
    }

    if (v > 0 && v % VERTEX_CHUNK_SIZE === 0) await yieldToBrowser();
  }

  return { skinIndices, skinWeights };
}
